import React, { createContext, useState } from 'react';
import { CalendarToday, TableRows } from '@mui/icons-material';
import { Container, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import _ from 'lodash';
import qs from 'qs';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { EventItemQueryParams } from '../../../types/Event';
import { PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { UserConfigurableModule } from '../../../types/User';
import { useEventFilters } from '../../../utility/event';
import AppLink from '../../common/AppLink/AppLink';
import TemplateModal from '../../events/TemplateModal/TemplateModal';
import ListHeader from '../../ListHeader';
import FilterSidebar from '../../common/filters/FilterSidebar';
import { listPageLayoutStyles } from '../../../styles/sxStyling';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { FilterState } from '../../common/filters/interfaces';
import EventTable from '../../events/EventTable/EventTable';
import EventCalendar from '../../common/calenders/EventCalender/EventCalender';

export type EventsLayoutView = 'table' | 'calendar';

export const FilterContext = createContext<FilterState>(undefined);

const EventsLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const eventListView: EventsLayoutView = _.endsWith(location.pathname, ROUTE_PATHS.EVENTS_TABLE) ? 'table' : 'calendar';

  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [showModal, setShowModal] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>(undefined);

  const filterKey = eventListView === 'table' ? UserConfigurableModule.EVENTS : UserConfigurableModule.EVENTS_CALENDAR;

  const moduleFilters = useEventFilters(eventListView);

  const handleNewEvent = (templateUuid: string | null) => {
    const q: EventItemQueryParams = {};
    if (templateUuid) q.template = templateUuid;
    navigate(`/${ROUTE_PATHS.EVENTS}/new?${qs.stringify(q)}`);
  };

  return (
    <>
      <Container maxWidth="xl">
        <ListHeader
          title="Events"
          module={filterKey}
          moduleFilters={moduleFilters}
          filtersOpen={sidebarOpen}
          onToggleFilters={() => setSidebarOpen((o) => !o)}
          onNew={() => setShowModal(true)}
        />

        <Box sx={listPageLayoutStyles}>
          <FilterSidebar
            moduleFilters={moduleFilters}
            module={filterKey}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <ToggleButtonGroup sx={{ mb: 1 }} value={eventListView} exclusive>
              <AppLink to={`/${ROUTE_PATHS.EVENTS_TABLE}`} sx={{ lineHeight: 0 }}>
                <ToggleButton value="table" size="small">
                  <TableRows />
                </ToggleButton>
              </AppLink>
              <AppLink to={`/${ROUTE_PATHS.EVENTS_CALENDAR}`} sx={{ lineHeight: 0 }}>
                <ToggleButton value="calendar" size="small">
                  <CalendarToday />
                </ToggleButton>
              </AppLink>
            </ToggleButtonGroup>
            <FilterContext.Provider value={filterState}>
              {eventListView === 'calendar' ?
                <EventCalendar /> :
                <EventTable />
              }
            </FilterContext.Provider>
          </Box>
        </Box>
      </Container>

      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={handleNewEvent}
      />
    </>
  );
};

export default EventsLayout;
